/**
 * Utility functions for parsing and building audience criterion JSON
 * Used by AudienceDetail.vue and AddAudience.vue
 */

/**
 * Drop values that cannot be shown or matched as an option
 * A null slipping into a Multiselect option list crashes it: optionsToArray
 * keeps null (typeof null === 'object') and isSelected then reads null.group
 * @param {Array} values - Raw values from a criterion or the UI
 * @returns {Array} Values safe to store and to offer as options
 */
export function sanitizeCriterionValues(values) {
  if (!Array.isArray(values)) return []
  return values.filter(v => v !== null && v !== undefined && v !== '')
}

/**
 * Parse a single criterion node into the UI format
 * @param {Object} c - Criterion object with column_name, operator, values
 * @returns {Object} UI criterion format
 */
export function parseSingleCriterion(c) {
  const column = c.column_name || c.column
  const operator = (c.operator || 'in').toUpperCase()

  if (operator === 'BETWEEN') {
    return {
      column,
      condition: 'BETWEEN',
      values: [],
      lowerbound: c.values?.[0] || null,
      upperbound: c.values?.[1] || null,
      singleValue: null,
    }
  }

  const values = sanitizeCriterionValues(c.values)

  // The condition comes from the stored operator, never from how many values it
  // holds: 'equal' is written for EQUAL and 'in' for IN, so what the user picked
  // is what comes back
  if (['EQUAL', 'EQ', '='].includes(operator)) {
    return {
      column,
      condition: 'EQUAL',
      values: [],
      lowerbound: null,
      upperbound: null,
      singleValue: values[0] ?? null,
    }
  }

  return {
    column,
    condition: 'IN',
    values,
    lowerbound: null,
    upperbound: null,
    singleValue: null,
  }
}

/**
 * Recursively flatten nested criterion structure to extract all leaf criteria
 * @param {Object} criterion - Nested criterion object
 * @returns {Array} Array of parsed leaf criteria
 */
export function flattenCriteriaToLeaves(criterion) {
  const leaves = []

  const traverse = (node) => {
    if (!node) return

    // If it's a leaf criterion (has column_name), add it
    if (node.column_name || node.column) {
      leaves.push(parseSingleCriterion(node))
    } else if (node.operands && Array.isArray(node.operands)) {
      // Recursively traverse operands
      node.operands.forEach(op => traverse(op))
    } else if (node.operand) {
      // Handle 'not' operator with single operand
      traverse(node.operand)
    }
  }

  traverse(criterion)
  return leaves
}

/**
 * Parse criterion JSON into simple criteria format (flat list)
 * @param {Object} criterion - Criterion object
 * @returns {Array} Array of simple criteria {column, values}
 */
export function parseSimpleCriteria(criterion) {
  if (!criterion) return [{ column: null, values: [] }]

  // Single criterion
  if (criterion.column_name || criterion.column) {
    return [{
      column: criterion.column_name || criterion.column,
      values: sanitizeCriterionValues(criterion.values)
    }]
  }

  // For nested structures, flatten all leaf criteria
  if (criterion.operands && Array.isArray(criterion.operands)) {
    const leaves = []
    const extractLeaves = (node) => {
      if (!node) return
      if (node.column_name || node.column) {
        leaves.push({
          column: node.column_name || node.column,
          values: sanitizeCriterionValues(node.values)
        })
      } else if (node.operands && Array.isArray(node.operands)) {
        node.operands.forEach(op => extractLeaves(op))
      } else if (node.operand) {
        extractLeaves(node.operand)
      }
    }
    criterion.operands.forEach(op => extractLeaves(op))
    return leaves.length > 0 ? leaves : [{ column: null, values: [] }]
  }

  return [{ column: null, values: [] }]
}

/**
 * Parse criterion JSON into advanced criteria format (groups)
 * Preserves nested structures recursively
 * @param {Object} criterion - Criterion object
 * @returns {Object} { operator, groups } where groups is array of criterion groups
 */
export function parseAdvancedCriteria(criterion) {
  const defaultGroup = {
    operator: 'AND',
    criteria: [{ column: null, condition: 'IN', values: [], lowerbound: null, upperbound: null, singleValue: null }],
    groups: []
  }

  if (!criterion) {
    return { operator: 'OR', negated: false, groups: [defaultGroup] }
  }

  // Only and/or are group operators here - a bare leaf criterion carries a
  // comparison operator ('in', 'between', ...) that must not leak into the
  // group's AND/OR selector.
  const operator = normalizeGroupOperator(criterion.operator, 'OR')

  // Parse groups - preserve nested structures recursively
  let groups
  if (criterion.operands && Array.isArray(criterion.operands)) {
    const isLeaf = (op) => op && (op.column_name || op.column)
    if (criterion.operands.every(isLeaf)) {
      // A flat and/or over leaf criteria is one group holding all of them,
      // not one single-criterion group per operand
      groups = [{
        operator,
        criteria: criterion.operands.map(op => parseSingleCriterion(op)),
        groups: [],
      }]
    } else {
      groups = criterion.operands.map(group => parseCriterionGroup(group))
    }
  } else {
    // Single criterion
    groups = [{
      operator: 'AND',
      criteria: [parseSingleCriterion(criterion)],
      groups: []
    }]
  }

  return { operator, groups }
}

/**
 * Parse a single criterion group (can be leaf criterion or nested structure)
 * @param {Object} group - Criterion group object
 * @returns {Object} Parsed group with criteria and nested groups
 */
function parseCriterionGroup(group) {
  // If it's a leaf criterion (has column_name directly)
  if (group.column_name || group.column) {
    return {
      operator: 'AND',
      criteria: [parseSingleCriterion(group)],
      groups: []
    }
  }

  // If it has operands, it's a nested structure
  if (group.operands && Array.isArray(group.operands)) {
    const operator = (group.operator || 'and').toUpperCase()
    const criteria = []
    const nestedGroups = []

    // Separate leaf criteria from nested groups
    group.operands.forEach(operand => {
      if (operand.column_name || operand.column) {
        // Leaf criterion
        criteria.push(parseSingleCriterion(operand))
      } else if (operand.operands && Array.isArray(operand.operands)) {
        // Nested group - recursively parse it
        nestedGroups.push(parseCriterionGroup(operand))
      } else {
        // Single criterion
        criteria.push(parseSingleCriterion(operand))
      }
    })

    return {
      operator,
      // Only add empty criterion if there are no nested groups AND no criteria
      // If there are nested groups, we don't need an empty criterion placeholder
      criteria: criteria.length > 0 ? criteria : (nestedGroups.length === 0 ? [{ column: null, condition: 'IN', values: [], lowerbound: null, upperbound: null, singleValue: null }] : []),
      groups: nestedGroups
    }
  }

  // Fallback: single criterion
  return {
    operator: 'AND',
    criteria: [parseSingleCriterion(group)],
    groups: []
  }
}

/**
 * Check if a criterion structure is "simple" (can be represented as flat AND list)
 * @param {Object} criterion - Criterion object
 * @returns {boolean}
 */
export function isSimpleCriterion(criterion) {
  if (!criterion) return true

  // Simple mode only holds column + values, so it can only represent 'in'.
  // A 'between' or 'equal' leaf shown there would be rewritten as 'in' on save,
  // which changes what the audience matches.
  const isPlainIn = (node) => String(node.operator || 'in').toLowerCase() === 'in'

  // Simple if: single operand OR (and/or with flat operands, no nested groups)
  if (criterion.column_name || criterion.column) return isPlainIn(criterion)
  if ((criterion.operator === 'and' || criterion.operator === 'or') && criterion.operands) {
    return !criterion.operands.some(op => op.operands) && criterion.operands.every(isPlainIn)
  }
  return false
}

/**
 * Calculate the nesting depth of a criterion structure
 * @param {Object} criterion - Criterion object
 * @param {number} currentDepth - Current depth (for recursion)
 * @returns {number} Maximum nesting depth
 */
export function getCriterionNestingDepth(criterion, currentDepth = 0) {
  if (!criterion) return currentDepth

  // Leaf node (has column_name)
  if (criterion.column_name || criterion.column) {
    return currentDepth
  }

  let maxChildDepth = currentDepth

  if (criterion.operands && Array.isArray(criterion.operands)) {
    criterion.operands.forEach(op => {
      const childDepth = getCriterionNestingDepth(op, currentDepth + 1)
      maxChildDepth = Math.max(maxChildDepth, childDepth)
    })
  }

  if (criterion.operand) {
    const childDepth = getCriterionNestingDepth(criterion.operand, currentDepth + 1)
    maxChildDepth = Math.max(maxChildDepth, childDepth)
  }

  return maxChildDepth
}

/**
 * Build criterion JSON from simple criteria
 * @param {Array} criteria - Array of {column, values}
 * @returns {Object} Criterion JSON object
 */
export function buildSimpleCriterionJson(criteria, operator = 'AND') {
  // A column with no values yet is still part of the criterion - the service
  // stores it, so dropping it here would silently delete a row the user can see
  const operands = criteria
    .filter(c => c.column)
    .map(c => ({
      column_name: c.column,
      operator: 'in',
      values: sanitizeCriterionValues(c.values),
    }))

  if (operands.length === 0) {
    return { criterion: {} }
  }

  const criterion = operands.length === 1 ? operands[0] : {
    operator: normalizeGroupOperator(operator).toLowerCase(),
    operands
  }

  return { criterion }
}

/**
 * Build criterion JSON from advanced criteria groups
 * Recursively builds nested structures
 * @param {Array} groups - Array of criterion groups
 * @param {Object} options - { operator: 'AND'|'OR', negated: boolean }
 * @returns {Object} Criterion JSON object
 */
export function buildAdvancedCriterionJson(groups, options = {}) {
  const { operator = 'OR', negated = false } = options

  const groupOperands = groups.map(group => buildCriterionGroupJson(group)).filter(g => g)

  if (groupOperands.length === 0) {
    return { criterion: {} }
  }

  let criterion
  if (groupOperands.length === 1) {
    criterion = groupOperands[0]
  } else {
    criterion = {
      operator: operator.toLowerCase(),
      operands: groupOperands,
    }
  }

  if (negated) {
    criterion = {
      operator: 'not',
      operand: criterion,
    }
  }

  return { criterion }
}

/**
 * Build JSON for a single criterion group (handles nested groups recursively)
 * @param {Object} group - Criterion group with criteria and optional nested groups
 * @returns {Object} Criterion JSON object for this group
 */
function buildCriterionGroupJson(group) {
  // Build leaf criteria operands
  const criteriaOperands = (group.criteria || [])
    .filter(c => c.column)
    .map(c => {
      if (c.condition === 'IN') {
        return {
          column_name: c.column,
          operator: 'in',
          values: sanitizeCriterionValues(c.values),
        }
      } else if (c.condition === 'BETWEEN') {
        return {
          column_name: c.column,
          operator: 'between',
          // an unfilled bound must not be written as null - it comes back as an
          // option and breaks the value dropdown
          values: sanitizeCriterionValues([c.lowerbound, c.upperbound]),
        }
      } else if (c.condition === 'EQUAL') {
        return {
          column_name: c.column,
          operator: 'equal',
          values: sanitizeCriterionValues([c.singleValue]),
        }
      }
      return null
    })
    .filter(c => c)

  // Build nested groups operands
  const nestedGroupOperands = (group.groups || [])
    .map(nestedGroup => buildCriterionGroupJson(nestedGroup))
    .filter(g => g)

  // Combine all operands
  const allOperands = [...criteriaOperands, ...nestedGroupOperands]

  if (allOperands.length === 0) {
    return null
  }

  // Always preserve the nested structure with operator and operands array
  // This ensures single-operand nested structures (like c2999) are preserved
  // as they were in the original JSON
  return {
    operator: (group.operator || 'and').toLowerCase(),
    operands: allOperands,
  }
}

/**
 * Pre-populate column values object from simple criteria
 * @param {Array} criteria - Array of {column, values}
 * @param {Object} existingValues - Existing columnValues object
 * @returns {Object} Updated columnValues object
 */
export function prePopulateColumnValues(criteria, existingValues = {}) {
  const newColumnValues = { ...existingValues }
  criteria.forEach(c => {
    const values = sanitizeCriterionValues(c.values)
    if (c.column && values.length > 0) {
      if (!newColumnValues[c.column]) {
        newColumnValues[c.column] = values
      } else {
        newColumnValues[c.column] = [...new Set([...newColumnValues[c.column], ...values])]
      }
    }
  })
  return newColumnValues
}

/**
 * Pre-populate column values object from advanced criteria groups
 * Recursively handles nested groups
 * @param {Array} groups - Array of criterion groups
 * @param {Object} existingValues - Existing columnValues object
 * @returns {Object} Updated columnValues object
 */
export function prePopulateColumnValuesFromGroups(groups, existingValues = {}) {
  const newColumnValues = { ...existingValues }
  
  const processGroup = (group) => {
    // Process leaf criteria in this group
    if (group.criteria) {
      group.criteria.forEach(c => {
        if (c.column) {
          let values = []
          if (c.condition === 'BETWEEN') {
            values = sanitizeCriterionValues([c.lowerbound, c.upperbound])
          } else if (c.condition === 'EQUAL') {
            values = sanitizeCriterionValues([c.singleValue])
          } else {
            values = sanitizeCriterionValues(c.values)
          }
          if (values.length > 0) {
            if (!newColumnValues[c.column]) {
              newColumnValues[c.column] = [...values]
            } else {
              newColumnValues[c.column] = [...new Set([...newColumnValues[c.column], ...values])]
            }
          }
        }
      })
    }
    
    // Recursively process nested groups
    if (group.groups) {
      group.groups.forEach(nestedGroup => processGroup(nestedGroup))
    }
  }
  
  groups.forEach(group => processGroup(group))
  return newColumnValues
}

/**
 * Extract values from histogram API response
 * @param {Object} result - API response
 * @returns {Array} Array of values
 */
export function extractValuesFromHistogram(result) {
  // Audsum format: { data: { header: ["attribute_name", "attribute_value", "count"], body: [["col", "val", 123]] } }
  if (result?.data?.body && Array.isArray(result.data.body)) {
    const header = result.data.header || []
    let valueIndex = header.indexOf('attribute_value')
    if (valueIndex === -1) valueIndex = 1  // Default to second column

    return result.data.body
      .map(row => row[valueIndex])
      .filter(v => v != null && v !== '' && v !== '-')
  }

  // Legacy formats
  if (result?.response && Array.isArray(result.response)) {
    return result.response.map(item => item.value || item[0]).filter(v => v)
  }
  if (Array.isArray(result?.data)) {
    return result.data.map(item => item.value || item[0]).filter(v => v)
  }

  return []
}

/**
 * Create empty criterion group for advanced mode
 * @returns {Object} Empty criterion group
 */
export function createEmptyCriterionGroup() {
  return {
    operator: 'AND',
    criteria: [{
      column: null,
      condition: 'IN',
      values: [],
      lowerbound: null,
      upperbound: null,
      singleValue: null,
    }],
    groups: []
  }
}

/**
 * Create empty simple criterion
 * @returns {Object} Empty simple criterion
 */
export function createEmptySimpleCriterion() {
  return { column: null, values: [] }
}

/**
 * Normalize a dataset schema response into a plain list of column names
 * Accepts { schema: { columns } } / { columns }, of strings or objects
 * @param {Object} schemaResult - Response from the audsum dataset endpoint
 * @returns {Array<string>} Column names
 */
export function normalizeSchemaColumns(schemaResult) {
  const columns = schemaResult?.schema?.columns || schemaResult?.columns || []

  return columns
    .map(c => (typeof c === 'string' ? c : (c?.name || c?.column_name || c?.attribute_name || c?.column)))
    .filter(c => typeof c === 'string' && c.length > 0)
}

/**
 * Collect every column name referenced by simple criteria and advanced groups
 * @param {Array} simpleCriteria - Array of {column, values}
 * @param {Array} criterionGroups - Array of criterion groups (may nest)
 * @returns {Array<string>} Distinct column names
 */
export function collectCriterionColumns(simpleCriteria = [], criterionGroups = []) {
  const columns = new Set()

  simpleCriteria.forEach(c => { if (c && c.column) columns.add(c.column) })

  const collectFromGroup = (group) => {
    if (!group) return
    ;(group.criteria || []).forEach(c => { if (c && c.column) columns.add(c.column) })
    ;(group.groups || []).forEach(collectFromGroup)
  }
  criterionGroups.forEach(collectFromGroup)

  return [...columns]
}

/**
 * Reduce a criterion node's operator to the AND/OR a group selector can hold
 * Leaf criteria carry comparison operators ('in', 'between', ...) which are not
 * group operators, so anything unrecognized falls back to the default
 * @param {string} operator - Raw operator from the criterion or the UI
 * @param {string} fallback - Value to use when the operator is not and/or
 * @returns {string} 'AND' or 'OR'
 */
function normalizeGroupOperator(operator, fallback = 'AND') {
  const raw = String(operator || '').toLowerCase()
  return (raw === 'and' || raw === 'or') ? raw.toUpperCase() : fallback
}

/**
 * Top-level AND/OR of a criterion, for the simple mode match selector
 * @param {Object} criterion - Criterion object
 * @returns {string} 'AND' or 'OR'
 */
export function getCriterionOperator(criterion) {
  if (!criterion || !criterion.operands) return 'AND'
  return normalizeGroupOperator(criterion.operator)
}

/**
 * Compare two criterion JSON strings by meaning rather than by text
 * A group wrapping a single operand is equivalent to that operand, so the same
 * criterion serialized from simple and from advanced mode compares equal
 * @param {string} a - Criterion JSON string
 * @param {string} b - Criterion JSON string
 * @returns {boolean}
 */
export function criterionJsonEquivalent(a, b) {
  const normalize = (node) => {
    if (!node || typeof node !== 'object') return node

    if (node.operand) {
      return { operator: node.operator, operand: normalize(node.operand) }
    }

    if (Array.isArray(node.operands)) {
      const operands = node.operands.map(normalize)
      // 'and'/'or' over a single operand adds no meaning
      if (operands.length === 1) return operands[0]
      return { operator: String(node.operator || '').toLowerCase(), operands }
    }

    return {
      column_name: node.column_name || node.column,
      operator: String(node.operator || '').toLowerCase(),
      values: node.values || [],
    }
  }

  try {
    const left = normalize(JSON.parse(a)?.criterion)
    const right = normalize(JSON.parse(b)?.criterion)
    return JSON.stringify(left) === JSON.stringify(right)
  } catch (e) {
    return a === b
  }
}

// Values a criterion actually carries, whatever condition it is shown as
function effectiveCriterionValues(c) {
  if (!c) return []
  if (c.condition === 'BETWEEN') return sanitizeCriterionValues([c.lowerbound, c.upperbound])
  if (c.condition === 'EQUAL') return sanitizeCriterionValues([c.singleValue])
  return sanitizeCriterionValues(c.values)
}

/**
 * Keep the IN/EQUAL labels the user picked when criteria are re-parsed
 *
 * Simple mode holds column + values only, so the JSON it produces says 'in' for
 * everything. Without this, visiting the Simple tab and coming back would turn
 * an EQUAL criterion into IN, and saving from Advanced would then rewrite the
 * stored operator - a silent data change from nothing but a tab click.
 *
 * @param {Array} groups - Freshly parsed criterion groups
 * @param {Array} previousGroups - Groups as the user last had them on screen
 * @returns {Array} Groups with the previous labels reapplied where they match
 */
export function preserveCriterionConditions(groups, previousGroups) {
  const keyOf = (c) => `${c.column}|${effectiveCriterionValues(c).join('\u0000')}`

  // repeated columns are matched in order, so two rows on the same column keep
  // their own labels
  const previousConditions = new Map()
  const collect = (group) => {
    if (!group) return
    ;(group.criteria || []).forEach(c => {
      const key = keyOf(c)
      if (!previousConditions.has(key)) previousConditions.set(key, [])
      previousConditions.get(key).push(c.condition)
    })
    ;(group.groups || []).forEach(collect)
  }
  ;(previousGroups || []).forEach(collect)

  const interchangeable = (condition) => condition === 'IN' || condition === 'EQUAL'

  const apply = (group) => ({
    ...group,
    criteria: (group.criteria || []).map(c => {
      const queue = previousConditions.get(keyOf(c))
      const previous = queue && queue.length ? queue.shift() : null

      if (!previous || previous === c.condition) return c
      if (!interchangeable(previous) || !interchangeable(c.condition)) return c

      const values = effectiveCriterionValues(c)
      return previous === 'EQUAL'
        ? { ...c, condition: 'EQUAL', values: [], singleValue: values[0] ?? null }
        : { ...c, condition: 'IN', values, singleValue: null }
    }),
    groups: (group.groups || []).map(apply),
  })

  return (groups || []).map(apply)
}

/**
 * Convert a criterion into the form the audsum query endpoints accept
 *
 * The stored record keeps the condition the user picked ('equal' vs 'in'), but
 * count/histogram/index/user_id queries reject it with
 * {"error":"unknown operator equal"}, so equality is sent as a single-value 'in'
 * - the same rows, in the vocabulary those endpoints know.
 *
 * @param {Object} criterion - Criterion as stored
 * @returns {Object} Criterion safe to send to a query endpoint
 */
export function toServiceCriterion(criterion) {
  if (!criterion || typeof criterion !== 'object') return criterion

  const node = { ...criterion }

  if (node.operand) node.operand = toServiceCriterion(node.operand)
  if (Array.isArray(node.operands)) node.operands = node.operands.map(toServiceCriterion)

  const isLeaf = node.column_name || node.column
  if (isLeaf && ['equal', 'eq', '='].includes(String(node.operator || '').toLowerCase())) {
    node.operator = 'in'
  }

  return node
}

/**
 * Whether a criterion holds nothing the service can filter on
 *
 * An empty criterion must be left out of a request entirely - sending
 * `criterion: {}` makes the count and histogram endpoints error, while omitting
 * the key asks about the whole dataset, which is what an audience with no
 * criteria means.
 *
 * @param {Object} criterion - Criterion, with or without its { criterion } wrapper
 * @returns {boolean} true when there is nothing to send
 */
export function isEmptyCriterion(criterion) {
  if (!criterion || typeof criterion !== 'object') return true
  if (criterion.criterion) return isEmptyCriterion(criterion.criterion)
  if (criterion.column_name || criterion.column) return false
  if (criterion.operand) return isEmptyCriterion(criterion.operand)
  if (Array.isArray(criterion.operands)) return criterion.operands.every(isEmptyCriterion)
  return true
}
